/**
 * Admin panel — rules CRUD, sync functionality, config display.
 * Extracted from app.js without logic changes.
 */

import { els } from "../dom";
import { getState, updateState, subscribe } from "../state";
import { formatNumber } from "../utils/formatting";
import { escapeHtml } from "../utils/escapeHtml";
import { onPanelActivate, getActivePanel } from "../router";
import { getProvider } from "../providerInstance";
import {
  unique,
  fillSelect,
  setFormMessage,
} from "./shared";
import { showToast } from "../components/Toast";
import { confirmDialog } from "../components/ConfirmDialog";
import { init as initAccessRules } from "./accessRules";
import { refreshAuditLog, logAction } from "../services/auditLog";
import { renderAuditLog } from "../components/AuditLog";

// ── Init ─────────────────────────────────────────────────────

export function init(): void {
  onPanelActivate("admin", render);
  subscribe("assets", render);
  subscribe("forecasts", render);
  subscribe("rules", render);
  subscribe("auditLog", render);
  bindAdminFlow();
  initAccessRules();

  document.getElementById("refresh-audit-btn")?.addEventListener("click", async () => {
    await refreshAuditLog();
    render();
  });

  refreshAuditLog();
}

// ── Render ───────────────────────────────────────────────────

export function render(): void {
  if (getActivePanel() !== "admin") return;
  renderAdminPanel();
  renderRuleFormOptions();
  renderConfigPanel();

  const auditContainer = document.getElementById("audit-log-container");
  if (auditContainer) {
    renderAuditLog(auditContainer, getState().auditLog as any[]);
  }
}

import { triggerRefresh } from "../refreshService";

// ── Admin flow ───────────────────────────────────────────────

function bindAdminFlow(): void {
  if (!els.addRuleBtn) return;

  els.syncValidatedBtn?.addEventListener("click", async () => {
    const provider = getProvider();
    // T7: confirm before running. The action rewrites every forecast entry
    // touched by the merge — it must not be one accidental click away.
    const confirmed = await confirmDialog({
      title: "Merge All Assets to Forecast?",
      message: "Synchronizes all validated assets with the current forecast entries. Updates forecast quantities based on the latest asset data.\n\nThis action cannot be undone.",
      confirmLabel: "Merge",
    });
    if (!confirmed) return;

    try {
      setFormMessage("Sync in progress...");
      (els.syncValidatedBtn as HTMLButtonElement).disabled = true;
      const result = await provider.syncValidatedToForecasts();
      await triggerRefresh();
      const updated = Number(result?.appendedForecasts || 0);
      setFormMessage(
        `Sync done: ${updated} forecast(s) added from ${result?.scannedAssets || 0} asset(s).`,
      );
      // T7: user-facing toast confirms completion at a glance
      await logAction("sync", "forecasts", "", `${updated} forecasts synced`);
      showToast(`Merge completed. ${updated} forecast entries updated.`, "success");
    } catch (err: any) {
      setFormMessage(err.message || "Sync failed.", true);
      showToast(err?.message || "Merge failed.", "error");
    } finally {
      (els.syncValidatedBtn as HTMLButtonElement).disabled = false;
    }
  });

  els.addRuleBtn.addEventListener("click", () => {
    updateState({ editingRuleId: null });
    openRuleForm();
  });

  els.cancelRuleBtn?.addEventListener("click", closeRuleForm);

  els.saveRuleBtn?.addEventListener("click", async () => {
    const provider = getProvider();
    const state = getState();
    const hub = (els.ruleHub as HTMLSelectElement).value.trim();
    const division = (els.ruleDivision as HTMLSelectElement).value.trim();
    const nbOfAssetParent = Number((els.ruleAssetParent as HTMLInputElement).value || 0);
    const nbOfAssetChild = Number((els.ruleAssetChild as HTMLInputElement).value || 0);
    const nbOfLanguages = Number((els.ruleLanguages as HTMLInputElement).value || 0);
    const languageList = (els.ruleLanguageList as HTMLInputElement)?.value.trim();
    const pctAssetTranslated = Number(
      String((els.ruleTranslationPct as HTMLInputElement).value || "0").replace(",", "."),
    );

    if (!hub || !division) {
      setFormMessage("HUB and Division are required.", true);
      return;
    }

    if (nbOfAssetParent <= 0 || nbOfAssetChild <= 0 || nbOfLanguages <= 0) {
      setFormMessage("Asset and language values must be > 0.", true);
      return;
    }

    if (
      !Number.isFinite(pctAssetTranslated) ||
      pctAssetTranslated < 0 ||
      pctAssetTranslated > 1
    ) {
      setFormMessage("% Asset translated must be between 0 and 1.", true);
      return;
    }

    const savedRule = await provider.upsertRule({
      id: state.editingRuleId || undefined,
      hub,
      division,
      nb_of_asset_parent: nbOfAssetParent,
      nb_of_asset_child: nbOfAssetChild,
      nb_of_languages: nbOfLanguages,
      language_list: languageList,
      "%_asset_translated": pctAssetTranslated,
    } as any);

    await logAction("upsert_rule", "rule", savedRule.id, `Rule for ${hub} / ${division}`);
    closeRuleForm();
    await triggerRefresh();
    setFormMessage("Rule saved.");
  });

  els.rulesTableBody?.addEventListener("click", async (event: Event) => {
    const btn = (event.target as HTMLElement).closest(
      "button[data-action]",
    ) as HTMLElement | null;
    if (!btn) return;

    const id = btn.dataset.id;
    const action = btn.dataset.action;
    const provider = getProvider();

    if (action === "edit") {
      const state = getState();
      const rule = state.rules.find((r) => r.id === id);
      if (!rule) return;

      updateState({ editingRuleId: id || null });
      openRuleForm(rule);
    }

    if (action === "delete" && id) {
      const shouldDelete = await confirmDialog({
        title: "Delete this rule?",
        message: "This forecast rule will be permanently removed.",
        confirmLabel: "Delete",
      });
      if (!shouldDelete) return;
      await provider.deleteRule(id);
      await logAction("delete_rule", "rule", id, "Rule deleted");
      await triggerRefresh();
      setFormMessage("Rule deleted.");
    }
  });
}

// ── Rule form ────────────────────────────────────────────────

function openRuleForm(rule: any = null): void {
  if (!els.ruleFormCard) return;
  els.ruleFormCard.classList.remove("hidden");

  if (!rule) {
    if (els.ruleFormTitle) els.ruleFormTitle.textContent = "Add Rule";
    if (els.ruleHub) (els.ruleHub as HTMLSelectElement).value = "";
    if (els.ruleDivision) (els.ruleDivision as HTMLSelectElement).value = "";
    if (els.ruleAssetParent) (els.ruleAssetParent as HTMLInputElement).value = "20";
    if (els.ruleAssetChild) (els.ruleAssetChild as HTMLInputElement).value = "5";
    if (els.ruleLanguages) (els.ruleLanguages as HTMLInputElement).value = "1";
    if (els.ruleLanguageList) (els.ruleLanguageList as HTMLInputElement).value = "";
    if (els.ruleTranslationPct) (els.ruleTranslationPct as HTMLInputElement).value = "0.8";
    return;
  }

  if (els.ruleFormTitle) els.ruleFormTitle.textContent = "Edit Rule";
  if (els.ruleHub) (els.ruleHub as HTMLSelectElement).value = rule.hub || "";
  if (els.ruleDivision) (els.ruleDivision as HTMLSelectElement).value = rule.division || "";
  if (els.ruleAssetParent)
    (els.ruleAssetParent as HTMLInputElement).value = String(rule.nbOfAssetParent || 20);
  if (els.ruleAssetChild)
    (els.ruleAssetChild as HTMLInputElement).value = String(rule.nbOfAssetChild || 5);
  if (els.ruleLanguages)
    (els.ruleLanguages as HTMLInputElement).value = String(rule.nbOfLanguages || 1);
  if (els.ruleLanguageList)
    (els.ruleLanguageList as HTMLInputElement).value = String(rule.languageList || "");
  if (els.ruleTranslationPct)
    (els.ruleTranslationPct as HTMLInputElement).value = String(
      rule.pctAssetTranslated ?? 0.8,
    );
}

function closeRuleForm(): void {
  if (els.ruleFormCard) els.ruleFormCard.classList.add("hidden");
  updateState({ editingRuleId: null });
}

function renderRuleFormOptions(): void {
  const state = getState();
  fillSelect(
    els.ruleHub,
    unique(state.assets.map((a) => a.hub)),
    "Select HUB",
  );
  fillSelect(
    els.ruleDivision,
    unique(state.assets.map((a) => a.division)),
    "Select Division",
  );
}

// ── Admin panel render ───────────────────────────────────────

export function renderAdminPanel(): void {
  const state = getState();
  if (els.adminAssetCount)
    els.adminAssetCount.textContent = String(state.assets.length);
  if (els.adminForecastCount)
    els.adminForecastCount.textContent = String(state.forecasts.length);

  const rows = state.rules.map(
    (rule) => `<tr>
    <td>${escapeHtml(rule.hub || "")}</td>
    <td>${escapeHtml(rule.division || "")}</td>
    <td>${escapeHtml(formatNumber(rule.nbOfAssetParent || 0))}</td>
    <td>${escapeHtml(formatNumber(rule.nbOfAssetChild || 0))}</td>
    <td>${escapeHtml(formatNumber(rule.nbOfLanguages || 0))}</td>
    <td>${escapeHtml(rule.languages || (rule as any).languageList || "")}</td>
    <td>${escapeHtml(
      (Number(rule.pctAssetTranslated || 0) * 100).toFixed(0),
    )}%</td>
    <td>
      <button class="btn btn-sm btn-ghost" data-action="edit" data-id="${escapeHtml(rule.id)}" type="button">Edit</button>
      <button class="btn btn-sm btn-danger" data-action="delete" data-id="${escapeHtml(rule.id)}" type="button">Delete</button>
    </td>
  </tr>`,
  );

  if (els.rulesTableBody)
    els.rulesTableBody.innerHTML =
      rows.join("") || '<tr><td colspan="8">No rules yet.</td></tr>';
}

// ── Config panel render ──────────────────────────────────────

function renderConfigPanel(): void {
  if (!els.configTypes) return;

  const state = getState();
  const types = unique(state.assets.map((a) => a.type));
  const divisions = unique(state.assets.map((a) => a.division));
  const brands = unique(state.assets.map((a) => a.brand));
  const hubs = unique(state.assets.map((a) => a.hub));
  const quarters = unique(state.assets.map((a) => a.quarter));

  const renderItems = (items: string[]) =>
    items
      .map((item) => `<div class="config-item">${escapeHtml(item)}</div>`)
      .join("");

  if (els.configTypes) els.configTypes.innerHTML = renderItems(types);
  if (els.configDivisions) els.configDivisions.innerHTML = renderItems(divisions);
  if (els.configBrands) els.configBrands.innerHTML = renderItems(brands);
  if (els.configHubs) els.configHubs.innerHTML = renderItems(hubs);
  if (els.configQuarters) els.configQuarters.innerHTML = renderItems(quarters);
}
